<template>
  <div class="sd-card p-6">
    <div class="sd-chart-card__header m-0 mb-4 pb-4">
      <h3 class="sd-chart-card__title">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        {{ $t('sales_dashboard.top_contracts') }}
      </h3>
      <span class="sd-badge sd-badge--primary">
        {{ $t('sales_dashboard.top_5') }}
      </span>
    </div>

    <div v-if="loading" class="space-y-4">
      <div v-for="i in 5" :key="i" class="animate-pulse flex items-center gap-4 p-3 rounded-lg bg-secondary-100">
        <div class="w-8 h-8 bg-secondary-200 rounded-full"></div>
        <div class="flex-1">
          <div class="h-4 bg-secondary-200 rounded w-3/4 mb-2"></div>
          <div class="h-3 bg-secondary-200 rounded w-1/2"></div>
        </div>
        <div class="h-6 bg-secondary-200 rounded w-24"></div>
      </div>
    </div>

    <div v-else-if="topContracts.length === 0" class="text-center py-8 text-secondary-400">
      <svg class="w-12 h-12 mx-auto mb-3 text-secondary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
      </svg>
      <p>{{ $t('sales_dashboard.no_contracts') }}</p>
    </div>

    <div v-else class="space-y-3">
      <div 
        v-for="(contract, index) in topContracts" 
        :key="contract._id || index"
        class="flex items-center gap-4 p-3 rounded-lg bg-secondary-50 hover:bg-secondary-100 transition-all border border-transparent hover:border-securdia-blue/20"
      >
        <!-- Rank badge -->
        <div :class="getRankClass(index)" class="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold shadow-sm">
          {{ index + 1 }}
        </div>
        
        <!-- Customer icon -->
        <div class="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-secondary-200">
          <svg class="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
          </svg>
        </div>
        
        <!-- Contract info -->
        <div class="flex-1 min-w-0">
          <p class="font-semibold text-secondary-800 truncate">
            {{ getCustomerName(contract) }}
          </p>
          <div class="flex items-center gap-2 text-sm text-secondary-500">
            <span>{{ getSellerName(contract) }}</span>
            <span v-if="getAgreementNumber(contract)" class="text-secondary-400">•</span>
            <span v-if="getAgreementNumber(contract)" class="font-mono text-xs text-secondary-400">{{ getAgreementNumber(contract) }}</span>
          </div>
        </div>
        
        <!-- Value and type -->
        <div class="text-right">
          <p class="font-bold text-lg text-securdia-blue">
            {{ formatCurrency(getContractValue(contract)) }}
          </p>
          <span :class="getTypeBadgeClass(contract)" class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full mt-1">
            {{ getContractTypeName(contract) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TopContracts',
  props: {
    contracts: {
      type: Array,
      default: () => []
    },
    salespersons: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    topContracts() {
      if (!this.contracts || this.contracts.length === 0) return [];
      
      return [...this.contracts]
        .map(c => ({
          ...c,
          totalValue: this.getContractValue(c)
        }))
        .sort((a, b) => b.totalValue - a.totalValue)
        .slice(0, 5);
    }
  },
  methods: {
    getContractValue(contract) {
      // Calculate total value based on contract type
      // Use both old and new field names for compatibility
      const monthlyValue = contract.monthlyValue || this.parseMoneyValue(contract.rental_month_cost) || 0;
      const cashValue = contract.cashValue || this.parseMoneyValue(contract.cash_sum) || 0;
      const contractPeriod = contract.contractPeriod || contract.contract_period || 36;
      const category = contract.category || contract.agreement_category;
      
      // Cash contracts
      if (category === 'cash' || category === 'cash_services' || (cashValue > 0 && monthlyValue === 0)) {
        return cashValue;
      }
      
      // For subscription/rental, calculate total contract value (period * monthly)
      return monthlyValue * contractPeriod;
    },
    
    parseMoneyValue(value) {
      if (!value) return 0;
      if (typeof value === 'number') return value;
      const cleaned = String(value).replace(/[^\d.-]/g, '');
      return parseFloat(cleaned) || 0;
    },
    
    getCustomerName(contract) {
      return contract.customerName || contract.customer_name || contract.customer?.name || this.$t('sales_dashboard.unknown_customer');
    },
    
    getSellerName(contract) {
      // Use seller field directly (now contains the name from API)
      if (contract.seller && !this.isObjectId(contract.seller)) {
        return contract.seller;
      }
      
      if (contract.seller_name) return contract.seller_name;
      
      const sellerId = contract.sellerId || contract.seller_id || contract.seller;
      if (!sellerId) return this.$t('sales_dashboard.unknown_seller');
      
      // Search in salespersons - check 'id' first since that's what the API returns
      const seller = this.salespersons.find(s => 
        s.id === sellerId || 
        s.seller_id === sellerId || 
        s._id === sellerId ||
        (s._id && s._id.toString() === sellerId)
      );
      
      if (seller) {
        return seller.name;
      }
      
      // If still not found, return "Okänd säljare" instead of the ObjectId
      console.log('[TopContracts] Could not find seller for ID:', sellerId);
      return this.$t('sales_dashboard.unknown_seller');
    },
    
    isObjectId(str) {
      // Check if string looks like a MongoDB ObjectId (24 hex chars)
      return typeof str === 'string' && /^[a-f\d]{24}$/i.test(str);
    },
    
    getAgreementNumber(contract) {
      return contract.agreementNumber || contract.agreement_number || null;
    },
    
    getContractTypeName(contract) {
      const category = contract.category || contract.agreement_category;
      const monthlyValue = contract.monthlyValue || this.parseMoneyValue(contract.rental_month_cost) || 0;
      
      const isCash = category === 'cash' || category === 'cash_services' || monthlyValue === 0;
      
      return isCash 
        ? this.$t('sales_dashboard.cash_sale') 
        : this.$t('sales_dashboard.subscription');
    },
    
    getRankClass(index) {
      const classes = [
        'bg-gradient-to-br from-yellow-400 to-yellow-500', // Gold
        'bg-gradient-to-br from-gray-300 to-gray-400',     // Silver
        'bg-gradient-to-br from-yellow-600 to-yellow-700', // Bronze
        'bg-primary-500',
        'bg-secondary-400'
      ];
      return classes[index] || 'bg-secondary-400';
    },
    
    getTypeBadgeClass(contract) {
      const category = contract.category || contract.agreement_category;
      const monthlyValue = contract.monthlyValue || this.parseMoneyValue(contract.rental_month_cost) || 0;
      
      const isCash = category === 'cash' || category === 'cash_services' || monthlyValue === 0;
      
      return isCash 
        ? 'bg-accent-100 text-accent-700'
        : 'bg-primary-100 text-primary-700';
    },
    
    formatCurrency(value) {
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
  }
};
</script>
